/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { initSignature } from '@/api/stripeApi';
import { appError, appRedirect, appSuccess } from '@/redux/actions/appActions';
import { trackEvent } from '@/utils/trackEvent'; // ✅ Ton helper GTM
import { instance as axios } from '@/api/baseUrl';
import { trackDatadogAction, trackDatadogError } from '@/services/datadog';

const Spacing = styled.div`
  height: 400px;
  @media (max-width: 600px) {
    height: 200px;
  }
`;


const SuccessPaymentPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const reduxContractReference = useAppSelector((state) => state.habitation.contractReference);
  // Fallback to localStorage if Redux state is lost after Stripe redirect
  const contractReference = reduxContractReference || localStorage.getItem('habitation_contractReference');

  // Extract stable user values to avoid re-renders
  const userEmail = useAppSelector(state => state.user?.email);
  const userFirstname = useAppSelector(state => state.user?.firstname);
  const userLastname = useAppSelector(state => state.user?.lastname);

  const signatureCalledRef = React.useRef(false);
  const isMountedRef = React.useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Protection : ne pas appeler plusieurs fois (résiste au StrictMode)
    if (!contractReference || signatureCalledRef.current) return;

    signatureCalledRef.current = true;
    dispatch(appRedirect());

    // Petit délai pour laisser les cookies se stabiliser après redirect Stripe
    const timer = setTimeout(async () => {
      if (!isMountedRef.current) return;

      // Track: arrivée sur la page après Stripe
      trackDatadogAction('habitation_stripe_redirect_success', {
        contractReference,
        source: reduxContractReference ? 'redux' : 'localStorage',
      });

      // Tenter un refresh du token avant d'appeler initSignature
      // pour s'assurer que les cookies sont bien établis après le redirect Stripe
      try {
        await axios.post('/security/token/refresh');
        trackDatadogAction('habitation_token_refresh_success', { contractReference });
      } catch (refreshError) {
        console.log('Token refresh after Stripe redirect:', refreshError);
        trackDatadogError(refreshError as Error, {
          action: 'habitation_token_refresh_failed',
          contractReference,
        });
        // Continuer même si le refresh échoue - l'interceptor gérera le retry
      }

      initSignature(contractReference)
        .then(res => {
          if (!isMountedRef.current) return;

          if (res.data.signatureUrl) {
            // Track: initSignature success
            trackDatadogAction('habitation_init_signature_success', {
              contractReference: res.data.contractReference,
              customerNumber: res.data.customerNumber,
            });

            // ✅ GTM event juste avant redirection
            // eslint-disable-next-line react-hooks/exhaustive-deps
            trackEvent('purchase', {
              ecommerce: {
                value: res.data.stripePrice,
                currency: 'EUR',
                payment_type: 'card',
                items: [{
                  item_category: 'Habitation',
                  price: res.data.stripePrice,
                  quantity: 1
                }]
              },
              user_data: {
                email: userEmail,
                first_name: userFirstname,
                last_name: userLastname,
              },
              contract_info: {
                contract_id: res.data.contractReference,
                customer_number: res.data.customerNumber,
                session_stripe: res.data.sessionStripe,
                start_date: res.data.startDate,
                end_date: res.data.endDate,
              }
            });

            dispatch(appSuccess());
            window.location.href = res.data.signatureUrl;
          } else {
            trackDatadogError(new Error('No signatureUrl in response'), {
              action: 'habitation_init_signature_no_url',
              contractReference,
            });
            dispatch(appError());
            console.error('Erreur lien signature Sell&Sign:');
          }
        })
        .catch(err => {
          if (!isMountedRef.current) return;

          trackDatadogError(err, {
            action: 'habitation_init_signature_failed',
            contractReference,
          });
          dispatch(appError());
          console.error('Erreur récupération clientSecret:', err);
        });
    }, 300); // 300ms de délai

    return () => clearTimeout(timer);
    // userEmail, userFirstname, userLastname sont intentionnellement omis des dépendances
    // car on veut capturer leur valeur au moment de l'effet, pas re-déclencher l'effet
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractReference, dispatch]);
  
  return (
      <Spacing />
  );
};

export default SuccessPaymentPage;
